import { Box, Button, IconButton, Link, Tooltip, Typography } from "@mui/material";
import Close from "@mui/icons-material/Close";
import Edit from "@mui/icons-material/Edit";
import moment from "moment";
import { useState } from "react";
import { api } from "../../../api/API";
import { Comment } from "../../../api/Tasks";
import { useCurrentUser } from "../../../misc/CurrentUser";
import { HtmlEditor } from "../../../misc/HtmlEditor";
import { HtmlView } from "../../../misc/HtmlView";
import { modKey } from "../../../misc/keys";
import { useSnackAction } from "../../../misc/SnackAction";
import { PanelAdd, panelFontSize, panelGutter, PanelRow, PanelSection } from "./PanelSection";

// editorHeight keeps a comment box short enough that the notes above it stay in view
const editorHeight = 90;

// the hint names the key the reader has in front of them
const submitHint = modKey + " + Enter";

export function TaskComments(props: {
    taskId: number;
    value: Comment[];
    onChange: (value: Comment[]) => void;
}) {
    const me = useCurrentUser();

    // body is the comment being written, draft the one being rewritten. adding opens
    // the box for a new comment, editing names the comment the draft belongs to.
    const [adding, setAdding] = useState(false);
    const [body, setBody] = useState("");
    const [editing, setEditing] = useState(0);
    const [draft, setDraft] = useState("");

    const post = useSnackAction(async (input: { body: string }) => {
        const saved = await api.tasks.setComment({ task: props.taskId, body: input.body });

        setAdding(false);
        setBody("");
        props.onChange(props.value.concat(saved));
    }, { success: "Posted" });

    const save = useSnackAction(async (input: { id: number; body: string }) => {
        const saved = await api.tasks.setComment({
            id: input.id,
            task: props.taskId,
            body: input.body,
        });

        setEditing(0);
        props.onChange(props.value.map(item => item.id === saved.id ? saved : item));
    }, { success: "Saved" });

    const remove = useSnackAction(async (input: { id: number }) => {
        await api.tasks.setComment({ id: input.id, task: props.taskId, archived: true });

        if (editing === input.id) setEditing(0);
        props.onChange(props.value.filter(item => item.id !== input.id));
    }, { success: "Removed" });

    const busy = post.loading || save.loading || remove.loading;

    return (
        <PanelSection label="Comments" noTopBorder>
            {props.value.map(comment => editing === comment.id ? (
                // the box for a rewrite runs the panel's width like the one for a new
                // comment, with the name and time kept above it
                <Box key={comment.id}>
                    <Box sx={{ px: 3, pt: 1, pb: 0.5 }}>
                        <CommentHeader comment={comment} />
                    </Box>
                    <HtmlEditor
                        minHeight={editorHeight}
                        fontSize={panelFontSize}
                        inset={panelGutter}
                        autoFocus
                        value={draft}
                        onChange={setDraft}
                        onSubmit={() => {
                            if (busy || isEmpty(draft)) return;
                            save.callback({ id: comment.id, body: draft });
                        }}
                        actions={
                            <>
                                <Button size="small" onClick={() => setEditing(0)}>Cancel</Button>
                                <Tooltip title={submitHint}>
                                    <span>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            disabled={busy || isEmpty(draft)}
                                            onClick={() => save.callback({ id: comment.id, body: draft })}
                                        >
                                            Save
                                        </Button>
                                    </span>
                                </Tooltip>
                            </>
                        }
                    />
                </Box>
            ) : (
                <PanelRow key={comment.id}>
                    <Box sx={{ flex: 1, minWidth: 0, py: 0.5 }}>
                        <CommentHeader comment={comment} />
                        <Box sx={{ fontSize: panelFontSize }}>
                            <HtmlView html={comment.body} />
                        </Box>
                    </Box>

                    {/* a comment is only ever changed by whoever left it (see core/task) */}
                    {me && me.id === comment.createdBy && (
                        <>
                            <IconButton
                                className="panel-row-action"
                                size="small"
                                sx={{ opacity: 0, flexShrink: 0 }}
                                disabled={busy}
                                onClick={() => {
                                    setDraft(comment.body);
                                    setEditing(comment.id);
                                }}
                            >
                                <Edit fontSize="inherit" />
                            </IconButton>
                            <IconButton
                                className="panel-row-action"
                                size="small"
                                sx={{ opacity: 0, flexShrink: 0 }}
                                disabled={busy}
                                onClick={() => remove.callback({ id: comment.id })}
                            >
                                <Close fontSize="inherit" />
                            </IconButton>
                        </>
                    )}
                </PanelRow>
            ))}

            {!adding ? (
                <PanelAdd>
                    <Box sx={{ display: "flex", alignItems: "center", minHeight: 32 }}>
                        <Link
                            component="button"
                            type="button"
                            variant="body2"
                            underline="hover"
                            color="text.secondary"
                            disabled={busy}
                            onClick={() => setAdding(true)}
                        >
                            Add a comment
                        </Link>
                    </Box>
                </PanelAdd>
            ) : (
                // the box for a new comment runs the panel's width, so it reads as the
                // place the section is writing to rather than another row
                <HtmlEditor
                    placeholder="Leave a note on this task..."
                    minHeight={editorHeight}
                    fontSize={panelFontSize}
                    inset={panelGutter}
                    autoFocus
                    value={body}
                    onChange={setBody}
                    onSubmit={() => {
                        if (busy || isEmpty(body)) return;
                        post.callback({ body: body });
                    }}
                    actions={
                        <>
                            <Button size="small" onClick={() => {
                                setAdding(false);
                                setBody("");
                            }}>Cancel</Button>
                            <Tooltip title={submitHint}>
                                <span>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={busy || isEmpty(body)}
                                        onClick={() => post.callback({ body: body })}
                                    >
                                        Post
                                    </Button>
                                </span>
                            </Tooltip>
                        </>
                    }
                />
            )}
        </PanelSection>
    );
}

function CommentHeader(props: { comment: Comment }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: panelFontSize }} noWrap>
                {props.comment.createdByName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
                {commentTime(props.comment)}
            </Typography>
        </Box>
    );
}

// commentTime says when the note was left, and marks one that was rewritten since.
function commentTime(comment: Comment) {
    const written = moment(comment.createdAt).format("MMM D, YYYY h:mm a");
    return moment(comment.updatedAt).isAfter(comment.createdAt) ? written + " (edited)" : written;
}

// the editor holds markup even when it reads as empty, so the text is what says
// there's nothing to post
function isEmpty(html: string) {
    return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim() === "";
}
